import { useState, useEffect, useRef, useCallback } from 'react';
import { getEnabledPlatforms } from '../utils/accountSettingPlatformList';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faPlus,
  faChevronDown,
  faChevronUp,
  faTimes,
  faUpload,
  faCheck,
  faSearch,
  faFileExcel,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { getFolderDisplayLabel } from '../utils/folderHierarchy';
import { useAccount } from '../contexts/AccountContext';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { useChannelConfigs } from '../hooks/useChannelConfigs';
import { getUserData } from '../utils/userUtils';
import './CampaignCreation.css';
import './CampaignQACreation.css';

/**
 * CampaignQACreation component for creating QA validation jobs
 * Supports DV360 (Excel template) and Facebook (PDF IO/media plan) platforms
 */
const CampaignQACreation = () => {
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();
  const { settings: accountSettings } = useAccountSettings(accountId, user);
  const { getChannelsForPlatform, hasMultipleChannels, isLoading: channelConfigsLoading } = useChannelConfigs(accountId, user);

  // Get enabled platforms from account settings
  const enabledPlatforms = getEnabledPlatforms(accountSettings?.['campaign-qa-enabled']);

  // Component state
  const [jobName, setJobName] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showNewFolderForm, setShowNewFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Platform and campaign fields
  const [platform, setPlatform] = useState('');
  const [advertiserId, setAdvertiserId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState(null);

  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Folder search and pagination state
  const [folderSearchTerm, setFolderSearchTerm] = useState('');
  const [folderPage, setFolderPage] = useState(1);
  const [folderPagination, setFolderPagination] = useState(null);
  const [isLoadingMoreFolders, setIsLoadingMoreFolders] = useState(false);
  const folderDropdownRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-select platform when enabledPlatforms loads
  useEffect(() => {
    if (enabledPlatforms.length > 0 && !platform) {
      setPlatform(enabledPlatforms[0]);
    }
  }, [enabledPlatforms.length]);

  // Auto-select channel config when platform changes
  useEffect(() => {
    if (platform) {
      const channels = getChannelsForPlatform(platform);
      if (channels.length === 1) {
        setSelectedChannelId(channels[0].id);
      } else {
        setSelectedChannelId(null);
      }
    }
  }, [platform, getChannelsForPlatform]);

  // Clear form fields when platform changes
  useEffect(() => {
    setUploadedFile(null);
    setCampaignId('');
    setAdvertiserId('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [platform]);

  // Fetch folders
  useEffect(() => {
    if (accountId) fetchFolders();
  }, [accountId]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!showFolderDropdown) {
      setFolderSearchTerm('');
      setFolderPage(1);
    }
  }, [showFolderDropdown]);

  // Handle folder search with debounce
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFolderPage(1);
      fetchFolders(folderSearchTerm, 1);
    }, 300);
  }, [folderSearchTerm]);

  const fetchFolders = async (search = '', page = 1, append = false) => {
    if (!accountId) return;
    try {
      if (page === 1) setIsLoading(true);
      else setIsLoadingMoreFolders(true);

      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: {
          accountId,
          userId: user?.userId || user?.id,
          search: search || undefined,
          page,
          limit: 20,
          // Name sort groups each sub-folder under its parent (Market ->
          // Advertiser), matching the other creation-flow pickers
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });

      const foldersData = response.data.folders || response.data || [];
      setFolders(prev => append ? [...prev, ...foldersData] : foldersData);
      if (response.data.pagination) setFolderPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching folders:', err);
      if (page === 1) setError('Failed to load folders');
    } finally {
      setIsLoading(false);
      setIsLoadingMoreFolders(false);
    }
  };

  const loadMoreFolders = useCallback(() => {
    if (folderPagination?.hasNextPage && !isLoadingMoreFolders) {
      const nextPage = folderPage + 1;
      setFolderPage(nextPage);
      fetchFolders(folderSearchTerm, nextPage, true);
    }
  }, [folderPagination, isLoadingMoreFolders, folderPage, folderSearchTerm]);

  const handleFolderScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) loadMoreFolders();
  }, [loadMoreFolders]);

  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    setShowFolderDropdown(false);
    setShowNewFolderForm(false);
  };

  const handleCreateNewFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      setIsLoading(true);
      const response = await api.post(getApiUrl('/api/v2/folders'), {
        name: newFolderName.trim(),
        accountId,
        userId: user?.userId || user?.id
      });
      const newFolder = response.data.folder || response.data;
      setFolders(prev => [newFolder, ...prev]);
      setSelectedFolder(newFolder);
      setShowNewFolderForm(false);
      setNewFolderName('');
      setShowFolderDropdown(false);
    } catch (err) {
      console.error('Error creating folder:', err);
      setError(err.response?.data?.error || 'Failed to create folder');
    } finally {
      setIsLoading(false);
    }
  };

  // Platform config
  const platformConfig = {
    dv360: { name: 'DV360', icon: 'smart_display' },
    facebook: { name: 'Facebook', icon: 'facebook' }
  };

  // File handling
  const validateFile = (file) => {
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (platform === 'facebook') {
      if (ext !== '.pdf') {
        return { valid: false, error: 'Please upload a PDF file (.pdf)' };
      }
    } else {
      const validExtensions = ['.xlsx', '.xls', '.xlsm'];
      if (!validExtensions.includes(ext)) {
        return { valid: false, error: 'Please upload an Excel file (.xlsx, .xls, .xlsm)' };
      }
    }
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 50MB' };
    }
    return { valid: true };
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validation = validateFile(file);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      setUploadedFile(file);
      setError(null);
      // Auto-generate job name from filename if not set
      if (!jobName) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setJobName(`QA: ${nameWithoutExt}`);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const validation = validateFile(file);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      setUploadedFile(file);
      setError(null);
      if (!jobName) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setJobName(`QA: ${nameWithoutExt}`);
      }
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!selectedFolder) {
      setError('Please select a folder');
      return;
    }
    if (!jobName.trim()) {
      setError('Please enter a job name');
      return;
    }
    if (!platform) {
      setError('Please select a platform');
      return;
    }
    if (platform === 'facebook' && hasMultipleChannels(platform) && !selectedChannelId) {
      setError('Please select a channel configuration');
      return;
    }
    if (platform === 'dv360' && !advertiserId.trim()) {
      setError('Please enter the DV360 Advertiser ID');
      return;
    }
    if (!campaignId.trim()) {
      setError(`Please enter the ${platform === 'facebook' ? 'Facebook' : 'DV360'} Campaign ID`);
      return;
    }
    if (!uploadedFile) {
      setError(platform === 'facebook'
        ? 'Please upload a PDF IO / media plan'
        : 'Please upload an Excel launch template');
      return;
    }

    try {
      setIsSubmitting(true);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', jobName.trim());
      if (platform === 'dv360') formData.append('advertiserId', advertiserId.trim());
      formData.append('campaignId', campaignId.trim());
      formData.append('platform', platform);
      if (selectedFolder) formData.append('folderId', selectedFolder.id);
      if (selectedChannelId) formData.append('channelId', selectedChannelId);
      formData.append('file', uploadedFile);

      // Create the QA job
      const response = await api.post(getApiUrl('/api/v2/campaign-qa'), formData, {
        params: { accountId, userId: user?.userId || user?.id },
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const jobId = response.data.id;

      // Automatically trigger the validation
      await api.post(getApiUrl(`/api/v2/campaign-qa/${jobId}/run`), {}, {
        params: { accountId, userId: user?.userId || user?.id }
      });

      // Navigate to the details page
      navigate(`/campaign-qa/${jobId}`);
    } catch (err) {
      console.error('Error creating QA job:', err);
      setError(err.response?.data?.error || 'Failed to create QA job');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="campaign-creation-container">
      <div className="creation-header">
        <h1>New Campaign QA Job</h1>
        <p>
          {platform === 'facebook'
            ? 'Validate a Facebook campaign against its IO / media plan'
            : 'Validate a DV360 campaign against its original launch template'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="creation-form">
        {error && <div className="form-error">{error}</div>}

        {/* Folder Selection */}
        <div className="form-group">
          <label>Select Folder *</label>
          <div className="folder-selector">
            <div className="folder-filter-container" ref={folderDropdownRef}>
              <button
                type="button"
                className={`folder-filter-button ${showFolderDropdown ? 'active' : ''}`}
                onClick={() => setShowFolderDropdown(!showFolderDropdown)}
              >
                <FontAwesomeIcon icon={faFolder} />
                <span>{selectedFolder ? getFolderDisplayLabel(selectedFolder) : 'Choose folder...'}</span>
                <FontAwesomeIcon icon={showFolderDropdown ? faChevronUp : faChevronDown} className="dropdown-arrow" />
              </button>
              {showFolderDropdown && (
                <div className="folder-filter-dropdown" onScroll={handleFolderScroll}>
                  <div className="folder-search-container">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search folders..."
                      value={folderSearchTerm}
                      onChange={(e) => setFolderSearchTerm(e.target.value)}
                      className="folder-search-input"
                    />
                  </div>
                  <button type="button" className="folder-filter-option new-folder" onClick={() => setShowNewFolderForm(true)}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Create new folder</span>
                  </button>
                  {showNewFolderForm && (
                    <div className="new-folder-form">
                      <input
                        type="text"
                        placeholder="Folder name"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        className="new-folder-input"
                        autoFocus
                      />
                      <button type="button" onClick={handleCreateNewFolder} className="new-folder-btn" disabled={!newFolderName.trim()}>
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                      <button type="button" onClick={() => { setShowNewFolderForm(false); setNewFolderName(''); }} className="new-folder-cancel">
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}
                  <div className="folder-filter-divider" />
                  {folders.map(folder => (
                    <button
                      key={folder.id}
                      type="button"
                      className={`folder-filter-option ${selectedFolder?.id === folder.id ? 'selected' : ''}`}
                      onClick={() => handleFolderSelect(folder)}
                    >
                      <FontAwesomeIcon icon={faFolder} />
                      <span>{getFolderDisplayLabel(folder)}</span>
                      {selectedFolder?.id === folder.id && <FontAwesomeIcon icon={faCheck} className="check-icon" />}
                    </button>
                  ))}
                  {isLoadingMoreFolders && <div className="loading-more">Loading more...</div>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Job Name */}
        <div className="form-group">
          <label htmlFor="jobName">Job Name *</label>
          <input
            type="text"
            id="jobName"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            placeholder="Enter a name for this QA job"
            className="form-control"
          />
        </div>

        {/* Platform Selection */}
        <div className="form-group">
          <label>Select Platform *</label>
          <div className="destination-grid" role="radiogroup" aria-label="Select platform">
            {enabledPlatforms.map(p => {
              const isSelected = platform === p;
              const config = platformConfig[p] || { name: p, icon: 'campaign' };
              return (
                <button
                  key={p}
                  type="button"
                  className={`destination-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => setPlatform(p)}
                  role="radio"
                  aria-checked={isSelected}
                >
                  <span className="material-symbols-outlined destination-icon">
                    {config.icon}
                  </span>
                  <span className="destination-name">{config.name}</span>
                  {isSelected && (
                    <FontAwesomeIcon icon={faCheck} className="check-icon" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Channel Configuration - for platforms with multiple configs */}
        {platform && hasMultipleChannels(platform) && (
          <div className="form-group">
            <label htmlFor="channel-config">Channel Configuration *</label>
            {channelConfigsLoading ? (
              <p className="form-help-text">Loading channel configurations...</p>
            ) : (
              <>
                <select
                  id="channel-config"
                  className="form-control"
                  value={selectedChannelId || ''}
                  onChange={(e) => setSelectedChannelId(e.target.value)}
                  required
                >
                  <option value="">Select a channel configuration</option>
                  {getChannelsForPlatform(platform).map(channel => (
                    <option key={channel.id} value={channel.id}>
                      {channel.name}
                    </option>
                  ))}
                </select>
                <p className="form-help-text">
                  Multiple {platformConfig[platform]?.name || platform} configurations are available. Select which one to use for this campaign.
                </p>
              </>
            )}
          </div>
        )}

        {/* DV360: Advertiser ID */}
        {platform === 'dv360' && (
          <div className="form-group">
            <label htmlFor="advertiserId">Advertiser ID *</label>
            <input
              type="text"
              id="advertiserId"
              className="form-control"
              placeholder="Enter DV360 Advertiser ID"
              value={advertiserId}
              onChange={(e) => setAdvertiserId(e.target.value)}
              required
            />
            <p className="form-help-text">Your DV360 Advertiser ID (e.g., 7965755403)</p>
          </div>
        )}

        {/* Campaign ID - all platforms */}
        <div className="form-group">
          <label htmlFor="campaignId">Campaign ID *</label>
          <input
            type="text"
            id="campaignId"
            className="form-control"
            placeholder={platform === 'facebook' ? 'Enter Facebook Campaign ID' : 'Enter DV360 Campaign ID'}
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            required
          />
          <p className="form-help-text">
            {platform === 'facebook'
              ? 'Your Facebook Campaign ID (e.g., 120241760125970096)'
              : 'Your DV360 Campaign ID (e.g., 56512714)'}
          </p>
        </div>

        {/* File Upload */}
        <div className="form-group">
          <label>{platform === 'facebook' ? 'Upload IO / Media Plan *' : 'Upload Launch Template *'}</label>
          <div
            className={`file-upload-zone ${isDragOver ? 'drag-over' : ''} ${uploadedFile ? 'has-file' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept={platform === 'facebook' ? '.pdf' : '.xlsx,.xls,.xlsm'}
              style={{ display: 'none' }}
            />
            {uploadedFile ? (
              <div className="uploaded-file">
                <FontAwesomeIcon icon={platform === 'facebook' ? faFilePdf : faFileExcel} className={`file-icon ${platform === 'facebook' ? 'pdf' : 'excel'}`} />
                <span className="file-name">{uploadedFile.name}</span>
                <button type="button" className="remove-file-btn" onClick={(e) => { e.stopPropagation(); removeFile(); }}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ) : (
              <div className="upload-placeholder">
                <FontAwesomeIcon icon={faUpload} className="upload-icon" />
                <p>{platform === 'facebook'
                  ? 'Drag and drop your PDF file here, or click to browse'
                  : 'Drag and drop your Excel file here, or click to browse'}</p>
                <span className="file-hint">{platform === 'facebook'
                  ? 'Supports .pdf files up to 50MB'
                  : 'Supports .xlsx, .xls, .xlsm files up to 50MB'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/campaign-qa')}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create & Run QA'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CampaignQACreation;
